import React from 'react';
import { KenticoDataContext } from '../../../../contexts/kentico-data-context';
import { parseJSONOrDefault } from '../../../../utils';
import {
  getSimpleRemoteConfig,
  getSplashConfig,
  isSplashBypassed,
} from '../../../../utils/remote-config-manager';
import { log } from '../../../../utils/log';
import Banner, {
  isOutageDismissed,
  dismissOutageLocal,
  ISplashContent,
} from '../../banner';
import { assetNames } from './asset-names';
import { SplashActive } from './splash-active';

/**
 * Keys for the CMS assets rendered on the landing page. Assets are keyed by
 * their Kontent asset name (see `getAllAssetReferences`), so these must match
 * the asset names configured in Kontent. Accessed with optional chaining so a
 * missing asset degrades gracefully rather than throwing.
 */
const templateIds = {
  logo: 'landing-unum-logo',
  title: 'landing-main-title',
  subtext: 'landing-main-subtext',
  registrationButton: 'landing-registration-button',
  link: 'landing-login-button',
  copyrightIconId: 'landing-copyright-image',
  copyrightTextId: 'landing-copyright-text',
  desktopImage: 'landing-desktop-image',
  mobileImage: 'landing-mobile-image',
  secondaryTitle: 'landing-secondary-title',
  secondarySubtext: 'landing-secondary-subtext',
  list: 'landing-list-container',
  spanishInfoIcon: 'spanish-info-icon',
};

// The outage warning detection and dismissal checks are shared with the
// site-wide banner so behavior stays consistent across the app. Re-exported
// here for consumers/tests that reference them from the landing module.
export { isOutageDismissed, dismissOutageLocal };

interface ICopyrightContainerProps {
  main?: boolean;
  imageId: string;
  textId: string;
  text: string;
  desktopImage?: string;
  mobileImage?: string;
}

/**
 * Renders the copyright line with the Unum icon. The desktop variant (`main`)
 * shows above the tablet breakpoint; the non-main variant shows at/below it.
 */
function CopyrightContainer(props: Readonly<ICopyrightContainerProps>) {
  const visibility = props.main
    ? 'tw-flex max-[980px]:tw-hidden'
    : 'tw-hidden max-[980px]:tw-flex max-[980px]:tw-ml-[54px]';

  return (
    <div
      className={`${visibility} tw-items-end tw-mb-[42px] tw-ml-[10%] tw-mr-[5%] max-[765px]:tw-mt-10 max-[765px]:tw-ml-[10%]`}
    >
      <div
        id={props.imageId}
        className="tw-min-w-[40px] tw-h-10 tw-mr-[15px] tw-bg-center tw-bg-no-repeat max-[980px]:tw-hidden"
        style={{
          backgroundImage: props.desktopImage
            ? `url(${props.desktopImage})`
            : undefined,
        }}
      />
      <div
        className="tw-min-w-[40px] tw-h-10 tw-mr-[15px] tw-bg-center tw-bg-no-repeat tw-hidden max-[980px]:tw-block"
        style={{
          backgroundImage: props.mobileImage
            ? `url(${props.mobileImage})`
            : undefined,
        }}
      />
      <p
        id={props.textId}
        className="tw-font-sans tw-font-normal tw-text-sm tw-m-0 tw-max-w-[500px] tw-text-[#2e2e2e] max-[980px]:tw-text-white"
      >
        {props.text}
      </p>
    </div>
  );
}

/**
 * Landing page shown on deployed (non-localhost) environments. Mirrors the
 * claimant-facing landing from the prior version of this site: CMS-driven
 * hero content, register/login actions, a Spanish-assistance callout, and an
 * optional outage warning banner.
 */
const RemoteLanding: () => React.ReactElement = () => {
  const [showOutage, setShowOutage] = React.useState(false);
  const [splashContent, setSplashContent] = React.useState<ISplashContent>();
  const [config, setConfig] = React.useState<Record<string, string>>({});
  const [showSpanish, setShowSpanish] = React.useState(false);
  const { assets } = React.useContext(KenticoDataContext);

  React.useEffect(() => {
    const run = async () => {
      try {
        // An active outage (a splash whose window currently includes "now")
        // takes over the whole page with the SplashActive content. Upcoming
        // outage warnings are handled by the shared <Banner /> below.
        const activeSplash = await getSplashConfig();

        if (activeSplash?.showSplash && !isSplashBypassed()) {
          setSplashContent(parseJSONOrDefault(activeSplash.content, {}));
          setShowOutage(true);
        }

        setConfig((await getSimpleRemoteConfig()) || {});
      } catch (e) {
        log.error('Unable to load landing configuration', e);
      }
    };

    run();
  }, []);

  if (showOutage) {
    return (
      <div id="remote-landing" className="tw-h-full">
        <SplashActive content={splashContent ?? {}} config={config} />
      </div>
    );
  }

  return (
    <div
      id="remote-landing"
      className="tw-flex tw-flex-col tw-h-full tw-overflow-y-auto"
    >
      <div className="tw-shrink-0">
        <Banner />
      </div>
      <div className="tw-flex tw-flex-row tw-max-h-[inherit] max-[980px]:tw-flex-col min-[765px]:tw-h-full">
        <div className="tw-flex tw-flex-col tw-justify-center tw-flex-1 tw-h-full tw-bg-unm-container-bg min-[765px]:tw-pb-[70px] min-[980px]:tw-block min-[980px]:tw-justify-evenly min-[980px]:tw-pb-0 max-[980px]:tw-flex-row max-[980px]:tw-justify-start">
          <div className="min-[980px]:tw-flex min-[980px]:tw-flex-col min-[980px]:tw-justify-between min-[980px]:tw-h-full">
            <div className="tw-mb-[60px] tw-mt-10 tw-pl-[10%] max-[765px]:tw-pl-[5%]">
              <img
                id={templateIds.logo}
                src={assets[assetNames.logo]?.url}
                alt=""
              />
            </div>
            <div className="tw-max-w-[540px] tw-mr-[5%] tw-ml-[10%] max-[765px]:tw-ml-[5%] max-[765px]:tw-pt-0 min-[980px]:tw-mr-[192px] min-[980px]:tw-self-end">
              <h1
                id={templateIds.title}
                className="tw-font-sans tw-font-light tw-text-[44px] tw-text-[#335E7A] tw-mb-[38px] tw-max-w-[400px] max-[980px]:tw-text-[40px]"
              >
                Welcome to Unum Total Leave
              </h1>
              <p
                id={templateIds.subtext}
                className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-[#2e2e2e]"
              >
                Our Portal is a simple and easy way to plan leave, receive
                benefits and smoothly navigate your return to work.
              </p>
              <div className="tw-pt-6 tw-flex tw-flex-col tw-items-start min-[1520px]:tw-flex-row min-[1520px]:tw-items-center">
                <a
                  id={templateIds.registrationButton}
                  href={config['registration_redirect_url']}
                  rel="noopener"
                  className="tw-font-sans tw-font-semibold tw-text-base tw-text-[#017f90] tw-bg-[#f6f8fa] tw-border tw-border-solid tw-border-[#017f90] tw-rounded-[30px] tw-min-w-[226px] tw-px-6 tw-py-3 tw-mr-2.5 tw-no-underline hover:tw-bg-[#0199a6] hover:tw-text-white hover:tw-no-underline"
                >
                  Register for an account
                </a>
                <a
                  id={templateIds.link}
                  href={config['login_redirect_url']}
                  rel="noopener"
                  className="tw-font-sans tw-font-semibold tw-text-base tw-text-white tw-bg-[#017f90] tw-border tw-border-solid tw-border-[#017f90] tw-rounded-[30px] tw-min-w-[226px] tw-px-6 tw-py-3 tw-mr-2.5 tw-no-underline hover:tw-bg-[#0199a6] hover:tw-text-white hover:tw-no-underline max-[1520px]:tw-my-[25px]"
                >
                  Login to existing account
                </a>
              </div>
              <div className="tw-block tw-my-12">
                <div className="tw-bg-[#eff3f8] tw-rounded-lg tw-font-sans tw-font-semibold tw-text-sm tw-p-4">
                  <img
                    id={templateIds.spanishInfoIcon}
                    src={assets[assetNames.informationIcon]?.url}
                    alt=""
                    className="tw-mr-2 tw-inline"
                  />
                  Quiere presentar o actualizar un reclamo en español?{' '}
                  <button
                    type="button"
                    className="tw-bg-transparent tw-border-none tw-font-sans tw-font-semibold tw-text-sm tw-p-0 tw-text-[#069] tw-underline tw-cursor-pointer"
                    onClick={() => setShowSpanish(!showSpanish)}
                  >
                    {showSpanish ? 'Ver menos' : 'Ver más'}
                  </button>
                </div>
                {showSpanish && (
                  <div className="tw-bg-[#eff3f8] tw-rounded-lg tw-font-sans tw-font-normal tw-text-sm tw-p-4 tw-w-full tw-max-w-[500px] tw-mt-px">
                    Si necesita asistancia en español para empezar un nuevo
                    reclamo, puede contactarnos llamando a 866-868-6737, de
                    lunes a viernes entre 8am y 8pm ET
                  </div>
                )}
              </div>
            </div>
            <CopyrightContainer
              main
              imageId={templateIds.copyrightIconId}
              textId={templateIds.copyrightTextId}
              text={`© ${new Date().getFullYear()} Unum Group. All rights reserved. Insurance products are underwritten by the subsidiaries of Unum Group. NS-580009`}
              desktopImage={assets[assetNames.unumIcon]?.url}
              mobileImage={assets[assetNames.unumIconWhite]?.url}
            />
          </div>
          <div className="tw-absolute tw-top-1/2 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2 max-[980px]:tw-relative max-[980px]:tw-flex max-[980px]:tw-top-0 max-[980px]:tw-left-0 max-[980px]:tw-translate-x-0 max-[980px]:tw-translate-y-0 max-[980px]:tw-max-h-[445px] max-[980px]:tw-items-end max-[980px]:tw-mt-[50px] max-[980px]:tw-pr-2.5 max-[765px]:tw-hidden">
            <img
              id={templateIds.desktopImage}
              src={assets[assetNames.desktopScreen]?.url}
              alt=""
            />
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-justify-center tw-flex-1 tw-h-full tw-bg-[#004470] min-[980px]:tw-flex min-[980px]:tw-justify-evenly max-[980px]:tw-flex-col max-[980px]:tw-justify-between max-[980px]:tw-pt-10">
          <div className="tw-max-w-[540px] tw-mr-[5%] tw-ml-[210px] max-[980px]:tw-pt-[41px] max-[980px]:tw-ml-[54px] max-[980px]:tw-max-w-full max-[765px]:tw-pt-[41px] max-[765px]:tw-ml-[5%]">
            <h2
              id={templateIds.secondaryTitle}
              className="tw-font-sans tw-font-semibold tw-text-[22px] tw-text-white"
            >
              What can you do?
            </h2>
            <p
              id={templateIds.secondarySubtext}
              className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-white max-[980px]:tw-max-w-full"
            >
              It's a centralized place to ask questions, file a claim and get
              support when you need it.
            </p>
            <div
              id={templateIds.list}
              className="tw-flex tw-flex-col tw-mt-10 max-[980px]:tw-flex-row max-[765px]:tw-flex-col max-[980px]:[&>:last-child]:tw-ml-[90px] max-[765px]:[&>:last-child]:tw-ml-0"
            >
              <div>
                <div className="tw-flex tw-flex-row tw-items-center tw-mb-4 [&>img]:tw-mr-[15px]">
                  <img src={assets[assetNames.checkmark]?.url} alt="" />
                  <p className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-white max-[980px]:tw-max-w-full">
                    Simple ways to upload information
                  </p>
                </div>
                <div className="tw-flex tw-flex-row tw-items-center tw-mb-4 [&>img]:tw-mr-[15px]">
                  <img src={assets[assetNames.checkmark]?.url} alt="" />
                  <p className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-white max-[980px]:tw-max-w-full">
                    Faster payments
                  </p>
                </div>
              </div>
              <div>
                <div className="tw-flex tw-flex-row tw-items-center tw-mb-4 [&>img]:tw-mr-[15px]">
                  <img src={assets[assetNames.checkmark]?.url} alt="" />
                  <p className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-white max-[980px]:tw-max-w-full">
                    Notifications the way you want
                  </p>
                </div>
                <div className="tw-flex tw-flex-row tw-items-center tw-mb-4 [&>img]:tw-mr-[15px]">
                  <img src={assets[assetNames.checkmark]?.url} alt="" />
                  <p className="tw-font-sans tw-font-normal tw-text-base tw-m-0 tw-max-w-[500px] tw-text-white max-[980px]:tw-max-w-full">
                    Quick answers and live support
                  </p>
                </div>
              </div>
            </div>
          </div>
          <img
            id={templateIds.mobileImage}
            src={assets[assetNames.desktopScreen]?.url}
            alt=""
            className="tw-hidden tw-max-w-[321px] tw-h-auto tw-ml-[5%] max-[765px]:tw-flex"
          />
          <CopyrightContainer
            imageId={templateIds.copyrightIconId}
            textId={templateIds.copyrightTextId}
            text={`© ${new Date().getFullYear()} Unum Group. All rights reserved. Insurance products are underwritten by the subsidiaries of Unum Group. NS-580009`}
            desktopImage={assets[assetNames.unumIcon]?.url}
            mobileImage={assets[assetNames.unumIconWhite]?.url}
          />
        </div>
      </div>
    </div>
  );
};

export { RemoteLanding };
